import { useState } from 'react';
import { User } from '../features/settings/types/user';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { countries } from '../lib/constants/countries';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ModeToggle } from '../components/ui/mode-toggle';
import { useClinicSettings } from '../lib/store/clinic-settings';
import { useToast } from '../hooks/use-toast';
import { Separator } from '../components/ui/separator';
import { Save, Plus, ChevronDown, Pencil } from 'lucide-react';
import { UserForm } from '../features/settings/components/UserForm';
import { UserList } from '../features/settings/components/UserList';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible"

export default function Settings() {
  const settings = useClinicSettings();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("general");
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const handleSave = () => {
    toast({
      title: "Settings saved",
      description: "Your clinic settings have been updated successfully.",
    });
  };

  return (
    <div className="container max-w-4xl mx-auto py-10 px-4">
      <div className="flex flex-col gap-8">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">Settings</h1>
          <p className="text-muted-foreground text-lg">
            Manage your clinic settings and preferences
          </p>
        </div>

        <Card className="border-2">
          <CardContent className="p-0">
            <Tabs defaultValue="general" className="w-full" onValueChange={setActiveTab}>
              <TabsList className="w-full justify-start border-b rounded-none h-12 bg-muted/40 px-6">
                <TabsTrigger 
                  value="general" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background px-4 h-12"
                >
                  General
                </TabsTrigger>
                <TabsTrigger 
                  value="appearance" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background px-4 h-12"
                >
                  Appearance
                </TabsTrigger>
                <TabsTrigger 
                  value="users" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background px-4 h-12"
                >
                  Users
                </TabsTrigger>
              </TabsList>
              
              <div className="p-6 pt-8">
                <TabsContent value="general" className="mt-0 space-y-8">
                  <div className="grid gap-8">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="clinicName" className="text-sm font-medium">
                          Clinic Name
                        </Label>
                        <Input
                          id="clinicName"
                          value={settings.clinicName}
                          onChange={(e) => settings.setClinicName(e.target.value)}
                          className="max-w-md"
                          placeholder="Enter clinic name"
                        />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="address" className="text-sm font-medium">
                          Address
                        </Label>
                        <Input
                          id="address"
                          value={settings.address}
                          onChange={(e) => settings.setAddress(e.target.value)}
                          className="max-w-md"
                          placeholder="Enter clinic address"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="country" className="text-sm font-medium">
                          Country
                        </Label>
                        <Select
                          value={settings.country}
                          onValueChange={settings.setCountry}
                        >
                          <SelectTrigger id="country" className="max-w-md">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent>
                            {countries.map((country) => (
                              <SelectItem key={country.code} value={country.code}>
                                {country.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-sm text-muted-foreground mt-1">
                          This will determine the phone number format for patients
                        </p>
                      </div>
                    </div>

                    <div className="grid gap-8 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-sm font-medium">
                          Phone
                        </Label>
                        <Input
                          id="phone"
                          value={settings.phone}
                          onChange={(e) => settings.setPhone(e.target.value)}
                          placeholder="Enter phone number"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-medium">
                          Email
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          value={settings.email}
                          onChange={(e) => settings.setEmail(e.target.value)}
                          placeholder="Enter email address"
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="appearance" className="mt-0">
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium leading-none tracking-tight">Theme</h3>
                      <p className="text-sm text-muted-foreground mt-2">
                        Choose between light and dark theme for the application.
                      </p>
                    </div>
                    <Separator className="my-6" />
                    <div className="max-w-md">
                      <ModeToggle />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="users" className="mt-0">
                  <div className="space-y-8">
                    <UserList onEdit={(user) => {
                      // Reset form and set new user
                      setEditingUser(null);
                      setTimeout(() => {
                        setEditingUser(user);
                        setIsFormOpen(true);
                      }, 0);
                    }} />
                    
                    <Separator />

                    <Collapsible open={isFormOpen} onOpenChange={setIsFormOpen}>
                      <CollapsibleTrigger asChild>
                        <Button variant="outline" className="gap-2 w-full">
                          {editingUser ? (
                            <>
                              <Save className="h-4 w-4" />
                              Edit User Settings
                            </>
                          ) : (
                            <>
                              <Plus className="h-4 w-4" />
                              Add New User
                            </>
                          )}
                          <ChevronDown className="h-4 w-4 ml-auto" />
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pt-6">
                        <div className="space-y-6">
                          <div>
                              <div className="mb-8">
                                {editingUser ? (
                                  <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                      <div className="bg-muted/50 border rounded-lg p-6 flex-1">
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                                          <Pencil className="h-4 w-4" />
                                          <span>Currently Editing User</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                          <div className="text-2xl font-semibold text-foreground">
                                            {editingUser.email}
                                          </div>
                                          <div className="text-sm px-2 py-1 rounded bg-primary/10 text-primary border border-primary/20">
                                            {editingUser.role}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex gap-2 ml-4">
                                        <Button
                                          variant="outline"
                                          className="gap-2"
                                          onClick={() => {
                                            setEditingUser(null);
                                          }}
                                        >
                                          <Plus className="h-4 w-4" />
                                          New User
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          className="gap-2"
                                          onClick={() => {
                                            setIsFormOpen(false);
                                            setEditingUser(null);
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 text-lg font-medium">
                                    <Plus className="h-5 w-5" />
                                    <span>New User Details</span>
                                  </div>
                                )}
                                <p className="text-sm text-muted-foreground mt-4">
                                  {editingUser 
                                    ? 'Modify user settings and permissions.'
                                    : 'Create a new user account and configure their permissions.'}
                                </p>
                              </div>
                              <Separator className="my-8" />
                          </div>
                          <UserForm 
                            mode={editingUser ? 'edit' : 'create'}
                            defaultValues={editingUser || undefined}
                            onSuccess={() => {
                              setIsFormOpen(false);
                              setEditingUser(null);
                            }}
                          />
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </CardContent>
        </Card>

        {/* Only show save button for general and appearance tabs */}
        {activeTab !== "users" && (
          <div className="flex justify-end">
            <Button 
              onClick={handleSave} 
              size="lg"
              className="gap-2 px-8"
            >
              <Save className="h-4 w-4" />
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
